import { RefObject, MutableRefObject } from "react";
import {
  RenderingEngine,
  Enums,
  setVolumesForViewports,
  volumeLoader,
  metaData as MetaData,
  getRenderingEngine,
  cache,
} from "@cornerstonejs/core";
import * as cornerstoneTools from "@cornerstonejs/tools";
import { init as csRenderInit } from "@cornerstonejs/core";
import { init as csToolsInit } from "@cornerstonejs/tools";
import { init as dicomImageLoaderInit } from "@cornerstonejs/dicom-image-loader";
import { createSegmentationManager } from "../../segmentation";
import createImageIdsAndCacheMetaData from "../../../../components/lib/panoptesCreateImageIdsAndCacheMetaData";
import setCtTransferFunctionForVolumeActor from "../../../../components/lib/setCtTransferFunctionForVolumeActor";
import { setupToolGroups } from "./toolSetup";
import { setupContourToLabelmapConverter } from "./contourToLabelmapConverter";
import * as polySeg from "@cornerstonejs/polymorphic-segmentation";
import { init as polySegInit } from "@cornerstonejs/polymorphic-segmentation";
const { ViewportType } = Enums;

interface InitCornerstoneProps {
  element1Ref: RefObject<HTMLDivElement>;
  element2Ref: RefObject<HTMLDivElement>;
  element3Ref: RefObject<HTMLDivElement>;
  setDataSource: (dataSource: string) => void;
  caseId?: string | number;
  editorMode?: boolean;
}

export const initCornerstone = async ({
  element1Ref,
  element2Ref,
  element3Ref,
  setDataSource,
  caseId,
  editorMode = false,
}: InitCornerstoneProps) => {
  // Initialize Cornerstone libraries
  await csRenderInit();
  // await csToolsInit();
  // @ts-ignore
  await csToolsInit({ addons: { polySeg } });
  dicomImageLoaderInit({ maxWebWorkers: 8 });

  // Initialize polymorphic segmentation (required for contour-to-labelmap conversion)
  polySegInit();

  // Define constants
  const volumeName = "CT_VOLUME_ID";
  const volumeLoaderScheme = "cornerstoneStreamingImageVolume";
  const volumeId = `${volumeLoaderScheme}:${volumeName}`;
  const toolGroupId = "MY_TOOLGROUP_ID";
  const volume3dToolGroupId = "VOLUME_3D_TOOLGROUP_ID";
  const renderingEngineId = "myRenderingEngine";

  const viewportId1 = "CT_AXIAL";
  const viewportId2 = "CT_SAGITTAL";
  const viewportId3 = "CT_CORONAL";
  const viewportId4 = "CT_3D_VOLUME"; // 3D volume viewport
  const viewportIds = [viewportId1, viewportId2, viewportId3];
  element1Ref.current.id = viewportId1;
  element2Ref.current.id = viewportId2;
  element3Ref.current.id = viewportId3;

  // Create and cache volume using the WADO-RS server
  // Use the passed case ID or fallback to a default
  const imageIds = await createImageIdsAndCacheMetaData(caseId);

  // Create the volume
  const volume = await volumeLoader.createAndCacheVolume(volumeId, {
    imageIds,
  });

  // Setup tool groups
  const { toolGroup, volume3dToolGroup } = await setupToolGroups(
    toolGroupId,
    volume3dToolGroupId,
    editorMode
  );

  // Setup rendering engine
  const renderingEngine = new RenderingEngine(renderingEngineId);

  // Define viewport configurations
  const viewportInputArray = [
    {
      viewportId: viewportId1,
      type: ViewportType.ORTHOGRAPHIC,
      element: element1Ref.current,
      defaultOptions: {
        orientation: Enums.OrientationAxis.AXIAL,
        // background: { x: 0, y: 0, z: 0 } as Types.Point3,
      },
    },
    {
      viewportId: viewportId2,
      type: ViewportType.ORTHOGRAPHIC,
      element: element2Ref.current,
      defaultOptions: {
        orientation: Enums.OrientationAxis.SAGITTAL,
        // background: { x: 0, y: 0, z: 0 } as Types.Point3,
      },
    },
    {
      viewportId: viewportId3,
      type: ViewportType.ORTHOGRAPHIC,
      element: element3Ref.current,
      defaultOptions: {
        orientation: Enums.OrientationAxis.CORONAL,
        // background: { x: 0, y: 0, z: 0 } as Types.Point3,
      },
    },
    // {
    //   viewportId: viewportId4,
    //   type: ViewportType.VOLUME_3D,
    //   element: element4Ref.current,
    //   defaultOptions: {
    //     // background: { x: 0, y: 0, z: 0 } as Types.Point3,
    //     orientation: Enums.OrientationAxis.CORONAL,
    //   },
    // },
  ];

  // Set viewports
  renderingEngine.setViewports(viewportInputArray);

  // Add viewports to the appropriate tool groups
  toolGroup.addViewport(viewportId1, renderingEngineId);
  toolGroup.addViewport(viewportId2, renderingEngineId);
  toolGroup.addViewport(viewportId3, renderingEngineId);
  volume3dToolGroup.addViewport(viewportId4, renderingEngineId);

  // Log image metadata for debugging
  const imageId = imageIds[0];
  const {
    rowCosines,
    columnCosines,
    imagePositionPatient,
    rowPixelSpacing,
    columnPixelSpacing,
    sliceThickness,
  } = MetaData.get("imagePlaneModule", imageId);

  console.log("Image Orientation Patient:", rowCosines, columnCosines);
  console.log("Image Position Patient:", imagePositionPatient);
  console.log("Pixel Spacing:", rowPixelSpacing, columnPixelSpacing);
  console.log("Slice Thickness:", sliceThickness);

  // Load the volume
  volume.load();

  // Set volumes for all viewports
  await setVolumesForViewports(
    renderingEngine,
    [
      {
        volumeId,
        callback: setCtTransferFunctionForVolumeActor,
      },
    ],
    viewportIds
  );

  // Enable linear interpolation on all viewports for smoother rendering
  // This smooths both the CT image and labelmap/segmentation overlays
  viewportIds.forEach((vpId) => {
    const vp = renderingEngine.getViewport(vpId);
    if (vp) {
      (vp as any).setProperties({
        interpolationType: Enums.InterpolationType.LINEAR,
      });
    }
  });

  // // Set up the 3D Volume viewport with proper rendering preset
  // const viewport3D = renderingEngine.getViewport(
  //   viewportId4
  // ) as any; // Cast to any to avoid TS errors
  //
  // // Create Refs to store and return
  // const volumeActorRef: MutableRefObject<any> = { current: null };
  // const clippingPlaneRef: MutableRefObject<any> = {
  //   current: vtkPlane.newInstance(),
  // };
  //
  // if (viewport3D) {
  //   // Set a CT preset for 3D rendering
  //   viewport3D.setProperties({
  //     preset: "CT-Soft-Tissue",
  //   });
  //
  //   // Prepare image data for picking
  //   prepareImageDataForPicking(viewport3D, volumeActorRef);
  //
  //   // Prevent context menu on 3D viewport
  //   if (element4Ref.current) {
  //     element4Ref.current.addEventListener("contextmenu", (e) => {
  //       e.preventDefault();
  //     });
  //   }
  // }

  // Initialize segmentation using the segmentation manager
  // Skip loading existing segmentations in editor mode
  let segmentationManager = null;
  if (!editorMode) {
    segmentationManager = await createSegmentationManager(
      {
        axial: viewportId1,
        sagittal: viewportId2,
        coronal: viewportId3,
        volume3D: viewportId4,
      },
      caseId
    );
  } else {
    console.log("Editor mode: Skipping segmentation loading from server");

    // In editor mode, create both labelmap and contour segmentations
    try {
      const labelmapSegmentationId = "DRAFT_SEGMENTATION";
      const contourSegmentationId = "DRAFT_CONTOUR_SEGMENTATION";

      // Create a supersampled labelmap for smoother in-plane contouring
      const SUPERSAMPLE = 2; // 2x in-plane resolution
      let derivedVolume = cache.getVolume(labelmapSegmentationId);
      if (!derivedVolume) {
        const ctVolume = cache.getVolume(volumeId);
        const [ctW, ctH, ctD] = ctVolume.dimensions;
        const [spX, spY, spZ] = ctVolume.spacing;

        // createLocalVolume caches one image per slice. If a prior mount's
        // volume entry was removed but the slice images stuck around, the
        // first slice id collides on re-creation. Sweep them defensively.
        for (let i = 0; i < ctD; i++) {
          const sid = `${labelmapSegmentationId}_slice_${i}`;
          if (cache.getImageLoadObject(sid)) {
            try {
              cache.removeImageLoadObject(sid);
            } catch (e) {
              /* ignore */
            }
          }
        }

        const ssW = ctW * SUPERSAMPLE;
        const ssH = ctH * SUPERSAMPLE;
        const ssSpX = spX / SUPERSAMPLE;
        const ssSpY = spY / SUPERSAMPLE;

        derivedVolume = volumeLoader.createLocalVolume(
          labelmapSegmentationId,
          {
            metadata: structuredClone(ctVolume.metadata),
            dimensions: [ssW, ssH, ctD] as any,
            spacing: [ssSpX, ssSpY, spZ] as any,
            origin: ctVolume.origin as any,
            direction: ctVolume.direction as any,
            targetBuffer: { type: "Uint8Array" },
            scalarData: new Uint8Array(ssW * ssH * ctD),
          }
        );

        // createLocalVolume doesn't forward referencedVolumeId to ImageVolume,
        // so set it manually — the brush tool needs this to find the CT volume.
        (derivedVolume as any).referencedVolumeId = volumeId;

        console.log(
          `Created supersampled labelmap: ${ssW}x${ssH}x${ctD} (${SUPERSAMPLE}x in-plane)`
        );
      } else {
        console.log(
          "Reusing existing labelmap volume from cache:",
          derivedVolume
        );
        // Clear the existing volume data to start fresh
        const voxelManager = derivedVolume.voxelManager;
        const dimensions = derivedVolume.dimensions;
        const dataLength = dimensions[0] * dimensions[1] * dimensions[2];
        for (let i = 0; i < dataLength; i++) {
          voxelManager.setAtIndex(i, 0);
        }
      }

      // Check if segmentation already exists before adding
      const existingLabelmap =
        cornerstoneTools.segmentation.state.getSegmentation(
          labelmapSegmentationId
        );
      if (!existingLabelmap) {
        // Add the labelmap segmentation to the state
        await cornerstoneTools.segmentation.addSegmentations([
          {
            segmentationId: labelmapSegmentationId,
            representation: {
              type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
              data: {
                volumeId: labelmapSegmentationId,
              },
            },
          },
        ]);
        console.log("Added labelmap segmentation to state");
      } else {
        console.log("Labelmap segmentation already exists in state");
      }

      // Check if contour segmentation already exists before adding
      const existingContour =
        cornerstoneTools.segmentation.state.getSegmentation(
          contourSegmentationId
        );
      if (!existingContour) {
        // Add contour segmentation for spline tool
        await cornerstoneTools.segmentation.addSegmentations([
          {
            segmentationId: contourSegmentationId,
            representation: {
              type: cornerstoneTools.Enums.SegmentationRepresentations.Contour,
            },
          },
        ]);
        console.log("Added contour segmentation to state");
      } else {
        console.log("Contour segmentation already exists in state");
      }

      // Add labelmap representation to all viewports
      await cornerstoneTools.segmentation.addLabelmapRepresentationToViewport(
        viewportId1,
        [
          {
            segmentationId: labelmapSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
          },
        ]
      );

      await cornerstoneTools.segmentation.addLabelmapRepresentationToViewport(
        viewportId2,
        [
          {
            segmentationId: labelmapSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
          },
        ]
      );

      await cornerstoneTools.segmentation.addLabelmapRepresentationToViewport(
        viewportId3,
        [
          {
            segmentationId: labelmapSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
          },
        ]
      );

      console.log("Added labelmap representation to all viewports");

      // Add contour representation to viewports for spline drawing
      await cornerstoneTools.segmentation.addContourRepresentationToViewport(
        viewportId1,
        [
          {
            segmentationId: contourSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Contour,
          },
        ]
      );

      await cornerstoneTools.segmentation.addContourRepresentationToViewport(
        viewportId2,
        [
          {
            segmentationId: contourSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Contour,
          },
        ]
      );

      await cornerstoneTools.segmentation.addContourRepresentationToViewport(
        viewportId3,
        [
          {
            segmentationId: contourSegmentationId,
            type: cornerstoneTools.Enums.SegmentationRepresentations.Contour,
          },
        ]
      );

      console.log("Added contour representation to all viewports");

      // Set active segmentation for the viewports (brush and other tools need this)
      cornerstoneTools.segmentation.activeSegmentation.setActiveSegmentation(
        viewportId1,
        labelmapSegmentationId
      );
      cornerstoneTools.segmentation.activeSegmentation.setActiveSegmentation(
        viewportId2,
        labelmapSegmentationId
      );
      cornerstoneTools.segmentation.activeSegmentation.setActiveSegmentation(
        viewportId3,
        labelmapSegmentationId
      );

      console.log("Set active segmentation to labelmap for all viewports");

      // Set active segment index to 1 (first segment after background)
      cornerstoneTools.segmentation.segmentIndex.setActiveSegmentIndex(
        labelmapSegmentationId,
        1
      );
      cornerstoneTools.segmentation.segmentIndex.setActiveSegmentIndex(
        contourSegmentationId,
        1
      );

      console.log("Set active segment index to 1 for both segmentations");

      // Get and log the segmentation state
      const allSegmentations =
        cornerstoneTools.segmentation.state.getSegmentations();
      console.log("All segmentations in state:", allSegmentations);

      const segRepresentations =
        cornerstoneTools.segmentation.state.getSegmentationRepresentations(
          toolGroupId
        );
      console.log(
        "Segmentation representations for toolGroup:",
        segRepresentations
      );

      console.log("Created labelmap and contour segmentations for editor mode");

      // Set up automatic conversion from contour to labelmap when spline annotations are completed
      const cleanupConverter = setupContourToLabelmapConverter(toolGroupId);
      console.log("Set up contour-to-labelmap converter");

      // Store the cleanup function to be called during deinit
      (toolGroup as any)._contourConverterCleanup = cleanupConverter;
    } catch (error) {
      console.error("Failed to create segmentations for editor mode:", error);
    }
  }

  // Render all viewports
  renderingEngine.render();

  // After volumes and labelmap reps are in, snap each viewport's camera back
  // to the CT volume's center, then recompute the crosshair tool center.
  // The supersampled labelmap leaves the focal point off-center otherwise, and
  // the crosshair tool intersects the three viewport centers to seed itself.
  // Defer one frame so the viewport canvases have measured client sizes.
  requestAnimationFrame(() => {
    try {
      const ctVolume = cache.getVolume(volumeId);
      const ctCenter: [number, number, number] | null = ctVolume
        ? (() => {
            const [dx, dy, dz] = ctVolume.dimensions;
            const idx: [number, number, number] = [
              Math.floor(dx / 2),
              Math.floor(dy / 2),
              Math.floor(dz / 2),
            ];
            const out: [number, number, number] = [0, 0, 0];
            ctVolume.imageData.indexToWorld(idx as any, out as any);
            return out;
          })()
        : null;

      viewportIds.forEach((vpId) => {
        const vp = renderingEngine.getViewport(vpId);
        if (!vp) return;
        if (ctCenter) {
          const cam = vp.getCamera();
          const offset = [
            ctCenter[0] - cam.focalPoint[0],
            ctCenter[1] - cam.focalPoint[1],
            ctCenter[2] - cam.focalPoint[2],
          ];
          vp.setCamera({
            focalPoint: ctCenter as any,
            position: [
              cam.position[0] + offset[0],
              cam.position[1] + offset[1],
              cam.position[2] + offset[2],
            ] as any,
          });
        }
      });
      renderingEngine.render();

      const crosshairs = toolGroup.getToolInstance(
        cornerstoneTools.CrosshairsTool.toolName
      ) as any;
      crosshairs?.computeToolCenter?.();
    } catch (error) {
      console.warn("Failed to recompute crosshair center:", error);
    }
  });

  // Return references to important objects
  return {
    volumeActorRef: { current: null },
    clippingPlaneRef: { current: null },
    segmentationManager,
    toolGroup,
    // Return cleanup function
    cleanup: () =>
      deinitCornerstone(
        renderingEngineId,
        toolGroupId,
        volume3dToolGroupId,
        volumeId
      ),
  };
};

/**
 * Properly cleanup all Cornerstone resources
 * @param renderingEngineId The ID of the rendering engine to cleanup
 * @param toolGroupId The ID of the main tool group
 * @param volume3dToolGroupId The ID of the 3D volume tool group
 * @param volumeId The ID of the volume to remove from cache
 */
export const deinitCornerstone = (
  renderingEngineId: string,
  toolGroupId: string,
  volume3dToolGroupId: string,
  volumeId: string
): void => {
  try {
    console.log("Starting Cornerstone cleanup...");

    // 0. First, get viewports and prepare for cleanup
    try {
      const renderingEngine = getRenderingEngine(renderingEngineId);
      if (renderingEngine) {
        // Get all viewports (v4 uses getViewports() which returns a Map)
        const viewports = renderingEngine.getViewports();
        viewports.forEach((viewport, viewportId) => {
          try {
            // Just log for now - no disable method in v4
            console.log(`Preparing to cleanup viewport: ${viewportId}`);
          } catch (error) {
            console.warn(
              `Error preparing viewport ${viewportId} for cleanup:`,
              error
            );
          }
        });
      }
    } catch (error) {
      console.warn("Error preparing viewports for cleanup:", error);
    }

    // 1. Cleanup contour-to-labelmap converter if it exists
    try {
      const toolGroup =
        cornerstoneTools.ToolGroupManager.getToolGroup(toolGroupId);
      if (toolGroup && (toolGroup as any)._contourConverterCleanup) {
        (toolGroup as any)._contourConverterCleanup();
        console.log("Cleaned up contour-to-labelmap converter");
      }
    } catch (error) {
      console.warn("Error cleaning up contour converter:", error);
    }

    // 2. Remove tool groups (v4 uses destroyToolGroup)
    try {
      cornerstoneTools.ToolGroupManager.destroyToolGroup(toolGroupId);
    } catch (error) {
      console.warn("Error destroying main tool group:", error);
    }

    try {
      cornerstoneTools.ToolGroupManager.destroyToolGroup(volume3dToolGroupId);
    } catch (error) {
      console.warn("Error destroying 3D tool group:", error);
    }

    // 3. Clear segmentations (v4 API)
    try {
      cornerstoneTools.segmentation.state.removeAllSegmentations();
    } catch (error) {
      console.warn("Error clearing segmentation state:", error);
    }

    // 4. Remove volumes from cache (v4 uses cache module)
    try {
      const volume = cache.getVolume(volumeId);
      if (volume) {
        cache.removeVolumeLoadObject(volumeId);
      }

      // Also remove segmentation volumes if they exist
      const labelmapVolume = cache.getVolume("DRAFT_SEGMENTATION");
      if (labelmapVolume) {
        const sliceCount = labelmapVolume.dimensions?.[2] ?? 0;
        for (let i = 0; i < sliceCount; i++) {
          const sid = `DRAFT_SEGMENTATION_slice_${i}`;
          if (cache.getImageLoadObject(sid)) {
            try {
              cache.removeImageLoadObject(sid);
            } catch (e) {
              /* ignore */
            }
          }
        }
        cache.removeVolumeLoadObject("DRAFT_SEGMENTATION");
        console.log("Removed DRAFT_SEGMENTATION from cache");
      }

      const contourVolume = cache.getVolume("DRAFT_CONTOUR_SEGMENTATION");
      if (contourVolume) {
        cache.removeVolumeLoadObject("DRAFT_CONTOUR_SEGMENTATION");
        console.log("Removed DRAFT_CONTOUR_SEGMENTATION from cache");
      }
    } catch (error) {
      console.warn("Error removing volumes from cache:", error);
    }

    // 5. Destroy rendering engine last
    try {
      const renderingEngine = getRenderingEngine(renderingEngineId);
      if (renderingEngine) {
        renderingEngine.destroy();
      }
    } catch (error) {
      console.warn("Error destroying rendering engine:", error);
    }

    console.log("Cornerstone cleanup completed");
  } catch (error) {
    console.error("Error during Cornerstone cleanup:", error);
  }
};
